import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { RiCheckLine, RiCloseLine } from '@remixicon/react'
import { countAnswered, isEmpty } from 'shared'
import type {
  OnboardingApplication,
  OnboardingApplicationDetail,
  OnboardingInviteChoice,
  OnboardingSession as OnboardingSessionData,
} from 'shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Drawer,
  EmptyState,
  Field,
  Input,
  CopyField,
  PageHeader,
  SectionSpinner,
  Select,
} from '../../components/ui'
import { formatAnswer } from '../../components/survey/AnswerList'
import { AccessCard, SessionCounts, SessionStatus } from '../../components/onboarding/SessionParts'
import { isAdmin as isAdminUser, useAuth } from '../../contexts/AuthContext'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'
import { formatDateTime } from '../../lib/dates'

/** One fact about the applicant, label above value. */
function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </dt>
      <dd className="mt-1 truncate text-sm font-medium text-gray-900 dark:text-gray-50">{value}</dd>
    </div>
  )
}

/**
 * What the operator reads before deciding, in a wide drawer: the answers can be
 * many and long, and a dialog is too tight for them. The decision stays in the
 * footer, so it is within reach however far down the answers go.
 */
function ReviewDrawer({
  applicationId,
  onClose,
  onDecided,
}: {
  applicationId: string | null
  onClose: () => void
  onDecided: () => void
}) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [detail, setDetail] = useState<OnboardingApplicationDetail | null>(null)
  const [company, setCompany] = useState('')
  const [invite, setInvite] = useState<OnboardingInviteChoice>('now')
  const [working, setWorking] = useState<'accept' | 'reject' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [outcome, setOutcome] = useState<{
    workspaceId: string
    url: string | null
    emailSent: boolean
  } | null>(null)

  useEffect(() => {
    // Closing keeps what is on screen until the drawer has slid away.
    if (!applicationId) return

    setDetail(null)
    setError(null)
    setOutcome(null)
    setInvite('now')

    // A slow answer for the application opened before must not land on this one.
    let cancelled = false

    api
      .get<{ data: OnboardingApplicationDetail }>(`/api/onboarding/applications/${applicationId}`)
      .then((response) => {
        if (cancelled) return
        setDetail(response.data)
        setCompany(response.data.company_name)
      })
      .catch((err) => !cancelled && setError(toErrorMessage(err) ?? 'error'))

    return () => {
      cancelled = true
    }
  }, [applicationId])

  const accept = async () => {
    if (!detail) return
    setWorking('accept')
    setError(null)
    try {
      const response = await api.post<{
        data: { workspace_id: string; invite_url?: string; email_status?: string }
      }>(`/api/onboarding/applications/${detail.id}/accept`, {
        company_name: company,
        invite,
      })
      setOutcome({
        workspaceId: response.data.workspace_id,
        url: response.data.invite_url ?? null,
        emailSent: response.data.email_status === 'sent',
      })
      onDecided()
    } catch (err) {
      setError(toErrorMessage(err) ?? 'error')
    } finally {
      setWorking(null)
    }
  }

  const reject = async () => {
    if (!detail) return
    setWorking('reject')
    setError(null)
    try {
      await api.post(`/api/onboarding/applications/${detail.id}/reject`, {})
      onDecided()
      onClose()
    } catch (err) {
      setError(toErrorMessage(err) ?? 'error')
    } finally {
      setWorking(null)
    }
  }

  const answered = detail ? countAnswered(detail.questions, detail.answers) : 0

  const deciding = detail?.status === 'submitted' && !outcome

  const footer = outcome ? (
    <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
      <Button variant="secondary" onClick={onClose} className="w-full sm:w-auto">
        {t('common.close')}
      </Button>
      <Button onClick={() => navigate(`/app/workspaces/${outcome.workspaceId}`)} className="w-full sm:w-auto">
        {t('onboarding.openWorkspace')}
      </Button>
    </div>
  ) : deciding && detail ? (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label={t('onboarding.companyName')}
          hint={t('onboarding.companyNameHint')}
          className="[@media(max-height:760px)]:[&_p]:hidden"
        >
          <Input value={company} onChange={(event) => setCompany(event.target.value)} maxLength={120} />
        </Field>
        <Field
          label={t('onboarding.inviteQuestion', { name: detail.person_name })}
          hint={t(`onboarding.invite_${invite}Help`)}
          className="[@media(max-height:760px)]:[&_p]:hidden"
        >
          <Select
            value={invite}
            onChange={(event) => setInvite(event.target.value as OnboardingInviteChoice)}
            options={[
              { value: 'now', label: t('onboarding.invite_now') },
              { value: 'wait', label: t('onboarding.invite_wait') },
            ]}
          />
        </Field>
      </div>
      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <ConfirmDialog
          title={t('onboarding.rejectTitle')}
          description={t('onboarding.rejectDescription', { name: detail.company_name })}
          confirmLabel={t('onboarding.reject')}
          onConfirm={reject}
          trigger={
            <Button variant="secondary" loading={working === 'reject'} className="w-full sm:w-auto">
              <RiCloseLine className="-ml-1 mr-1.5 size-4" aria-hidden="true" />
              {t('onboarding.reject')}
            </Button>
          }
        />
        <Button onClick={accept} loading={working === 'accept'} disabled={!company.trim()} className="w-full sm:w-auto">
          <RiCheckLine className="-ml-1 mr-1.5 size-4" aria-hidden="true" />
          {t('onboarding.accept')}
        </Button>
      </div>
    </div>
  ) : undefined

  return (
    <Drawer
      open={applicationId !== null}
      onClose={onClose}
      size="3xl"
      title={detail?.company_name ?? t('onboarding.review')}
      subtitle={detail ? `${detail.person_name} · ${detail.email}` : undefined}
      footer={footer}
    >
      {error && <Alert className="mb-4">{error === 'error' ? t('errors.generic') : error}</Alert>}

      {!detail && !error && <SectionSpinner />}

      {detail && outcome && (
        <div className="space-y-4">
          <Alert variant={invite === 'now' && !outcome.emailSent ? 'warning' : 'success'}>
            {t(
              invite === 'wait'
                ? 'onboarding.acceptedWait'
                : outcome.emailSent
                  ? 'onboarding.acceptedNow'
                  : 'onboarding.acceptedNoEmail',
            )}
          </Alert>
          {outcome.url && (
            <div>
              <p className="mb-1.5 text-xs text-gray-500 dark:text-gray-400">
                {t(outcome.emailSent ? 'onboarding.inviteSentHelp' : 'onboarding.inviteLinkHelp')}
              </p>
              <CopyField value={outcome.url} label={t('onboarding.inviteLink')} className="font-mono text-xs" />
            </div>
          )}
        </div>
      )}

      {detail && !outcome && (
        <div className="space-y-8">
          <section aria-labelledby="application-data">
            <h3
              id="application-data"
              className="text-sm font-semibold text-gray-900 dark:text-gray-50"
            >
              {t('onboarding.applicationData')}
            </h3>
            <Card className="mt-3 p-5">
              <dl className="grid gap-5 sm:grid-cols-2">
                <Fact label={t('onboarding.factCompany')} value={detail.company_name} />
                <Fact label={t('onboarding.factPerson')} value={detail.person_name} />
                <Fact label={t('onboarding.factEmail')} value={detail.email} />
                <Fact
                  label={t('onboarding.submittedAt')}
                  value={formatDateTime(detail.submitted_at, i18n.language)}
                />
              </dl>
            </Card>
          </section>

          <section aria-labelledby="application-answers">
            <div className="flex items-baseline justify-between gap-3">
              <h3
                id="application-answers"
                className="text-sm font-semibold text-gray-900 dark:text-gray-50"
              >
                {t('onboarding.answers')}
              </h3>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {t('onboardingApply.progress', { done: answered, total: detail.questions.length })}
              </span>
            </div>
            <Card className="mt-3 divide-y divide-gray-100 dark:divide-gray-800">
              {detail.questions.map((question, position) => {
                const blank = isEmpty(detail.answers[question.id])
                const text = formatAnswer(question, detail.answers[question.id] ?? null, i18n.language, t)
                return (
                  <div key={question.id} className="p-5">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      {position + 1}. {question.label}
                    </p>
                    <p
                      className={
                        blank
                          ? 'mt-1.5 text-sm text-gray-500 dark:text-gray-400'
                          : 'mt-1.5 whitespace-pre-wrap break-words text-sm text-gray-900 dark:text-gray-50'
                      }
                    >
                      {blank ? t('onboarding.noAnswer') : text}
                    </p>
                  </div>
                )
              })}
            </Card>
          </section>
        </div>
      )}
    </Drawer>
  )
}

/**
 * Open a closed or expired session again. An expired one needs a new date, and
 * the cap can be raised so that the people already in do not fill it up again.
 */
function ReopenDialog({
  session,
  open,
  onOpenChange,
  onDone,
}: {
  session: OnboardingSessionData
  open: boolean
  onOpenChange: (open: boolean) => void
  onDone: () => void
}) {
  const { t } = useTranslation()
  const [expiresAt, setExpiresAt] = useState('')
  const [max, setMax] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setError(null)
    setMax('')
    // A fortnight from today is the natural default, and the one the form offers.
    setExpiresAt(new Date(Date.now() + 14 * 86_400_000).toISOString().slice(0, 10))
  }, [open])

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await api.post(`/api/onboarding/sessions/${session.id}/reopen`, {
        expires_at: expiresAt,
        ...(max ? { max_applications: Number(max) } : {}),
      })
      onOpenChange(false)
      onDone()
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('onboarding.reopenTitle')}</DialogTitle>
          <DialogDescription>{t('onboarding.reopenDescription')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-4 space-y-4">
          <Field label={t('onboarding.newExpiry')} required>
            <Input type="date" value={expiresAt} onChange={(event) => setExpiresAt(event.target.value)} />
          </Field>
          <Field
            label={t('onboarding.maxApplications')}
            hint={t('onboarding.maxApplicationsKeep', { count: session.max_applications })}
          >
            <Input
              type="number"
              min={1}
              placeholder={String(session.max_applications)}
              value={max}
              onChange={(event) => setMax(event.target.value)}
            />
          </Field>
          {error && <Alert>{error}</Alert>}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={saving} disabled={!expiresAt}>
              {t('onboarding.reopen')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default function OnboardingSession() {
  const { t, i18n } = useTranslation()
  const { sessionId = '' } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const isAdmin = isAdminUser(user)
  const [reopening, setReopening] = useState(false)

  const [session, setSession] = useState<OnboardingSessionData | null>(null)
  const [applications, setApplications] = useState<OnboardingApplication[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reviewing, setReviewing] = useState<string | null>(null)
  // Changes when the code is replaced, so the access card reads it again.
  const [codeVersion, setCodeVersion] = useState(0)
  const [inviteUrl, setInviteUrl] = useState<string | null>(null)
  const [inviteSent, setInviteSent] = useState(false)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    const [sessionResponse, applicationsResponse] = await Promise.all([
      api.get<{ data: OnboardingSessionData }>(`/api/onboarding/sessions/${sessionId}`),
      api.get<{ data: OnboardingApplication[] }>(`/api/onboarding/sessions/${sessionId}/applications`),
    ])
    setSession(sessionResponse.data)
    setApplications(applicationsResponse.data)
  }, [sessionId])

  useEffect(() => {
    load()
      .catch((err) => setError(toErrorMessage(err) ?? 'error'))
      .finally(() => setLoading(false))
  }, [load])

  const act = async (path: 'code' | 'close') => {
    setBusy(true)
    setError(null)
    try {
      await api.post(`/api/onboarding/sessions/${sessionId}/${path}`, {})
      if (path === 'code') setCodeVersion((version) => version + 1)
      await load()
    } catch (err) {
      setError(toErrorMessage(err) ?? 'error')
    } finally {
      setBusy(false)
    }
  }

  const invite = async (applicationId: string) => {
    setError(null)
    try {
      const response = await api.post<{ data: { invite_url: string; email_status: string } }>(
        `/api/onboarding/applications/${applicationId}/invite`,
        {},
      )
      setInviteUrl(response.data.invite_url)
      setInviteSent(response.data.email_status === 'sent')
      await load()
    } catch (err) {
      setError(toErrorMessage(err) ?? 'error')
    }
  }

  return (
    <>
      <PageHeader
        title={session?.name ?? t('onboarding.title')}
        subtitle={session?.survey_title}
        backTo={{ href: '/app/onboarding', label: t('onboarding.title') }}
        actions={
          session && (
            <div className="flex flex-wrap items-center justify-end gap-2">
              <SessionStatus session={session} />
              {/* Closing, reopening and replacing the code decide who may apply: the admin's. */}
              {isAdmin && (
                <>
                  <ConfirmDialog
                    title={t('onboarding.newCodeTitle')}
                    description={t('onboarding.newCodeDescription')}
                    confirmLabel={t('onboarding.newCode')}
                    onConfirm={() => act('code')}
                    trigger={
                      <Button variant="secondary" disabled={busy}>
                        {t('onboarding.newCode')}
                      </Button>
                    }
                  />
                  {session.is_open ? (
                    <ConfirmDialog
                      title={t('onboarding.closeTitle')}
                      description={t('onboarding.closeDescription')}
                      confirmLabel={t('onboarding.close')}
                      onConfirm={() => act('close')}
                      trigger={
                        <Button variant="secondary" disabled={busy}>
                          {t('onboarding.close')}
                        </Button>
                      }
                    />
                  ) : (
                    <Button variant="secondary" disabled={busy} onClick={() => setReopening(true)}>
                      {t('onboarding.reopen')}
                    </Button>
                  )}
                </>
              )}
            </div>
          )
        }
      />

      {loading ? (
        <SectionSpinner />
      ) : (
        <div className="space-y-6">
          {error && <Alert>{error === 'error' ? t('errors.generic') : error}</Alert>}
          {session && !isAdmin && (
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('onboarding.adminOnlyHint')}</p>
          )}
          {session && <AccessCard sessionId={sessionId} reloadKey={codeVersion} canReplace={isAdmin} />}
          {inviteUrl && (
            // Not an Alert: that is a paragraph, and this holds paragraphs and a field.
            <div
              role="status"
              className="rounded-md bg-emerald-50 p-3 text-sm text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
            >
              <p>{t(inviteSent ? 'onboarding.inviteSentHelp' : 'onboarding.inviteLinkHelp')}</p>
              <div className="mt-2">
                <CopyField value={inviteUrl} label={t('onboarding.inviteLink')} className="font-mono text-xs" />
              </div>
              <button
                type="button"
                onClick={() => setInviteUrl(null)}
                className="mt-2 text-xs font-medium underline"
              >
                {t('common.close')}
              </button>
            </div>
          )}

          {session && <SessionCounts session={session} />}

          {applications.length === 0 ? (
            <EmptyState
              title={t('onboarding.noApplicationsTitle')}
              description={t('onboarding.noApplicationsDescription')}
            />
          ) : (
            <Card className="divide-y divide-gray-100 dark:divide-gray-800">
              {applications.map((application) => (
                <div key={application.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 p-4">
                  <div className="min-w-0 flex-1 basis-48">
                    <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-50">
                      {application.company_name}
                    </p>
                    <p className="truncate text-sm text-gray-500 dark:text-gray-400">
                      {application.person_name} · {application.email}
                    </p>
                  </div>
                  <span className="hidden text-xs text-gray-500 dark:text-gray-400 sm:block">
                    {formatDateTime(application.submitted_at, i18n.language)}
                  </span>
                  {application.status === 'submitted' ? (
                    <Button
                      onClick={() => setReviewing(application.id)}
                      aria-label={t('onboarding.reviewNamed', { name: application.company_name })}
                    >
                      {t('onboarding.reviewAction')}
                    </Button>
                  ) : (
                    <div className="flex flex-wrap items-center gap-2">
                      {application.invite_mode === 'waiting' ? (
                        <>
                          <Badge variant="warning">{t('onboarding.toInvite')}</Badge>
                          <Button
                            variant="secondary"
                            onClick={() => invite(application.id)}
                            aria-label={t('onboarding.inviteNamed', { name: application.person_name })}
                          >
                            {t('onboarding.inviteNow')}
                          </Button>
                        </>
                      ) : (
                        <Badge variant="success">{t('onboarding.accepted')}</Badge>
                      )}
                      {application.workspace_id && (
                        <Button
                          variant="ghost"
                          onClick={() => navigate(`/app/workspaces/${application.workspace_id}`)}
                        >
                          {t('onboarding.openWorkspace')}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </Card>
          )}
        </div>
      )}

      {session && (
        <ReopenDialog
          session={session}
          open={reopening}
          onOpenChange={setReopening}
          onDone={() => {
            load().catch(() => undefined)
          }}
        />
      )}

      <ReviewDrawer
        applicationId={reviewing}
        onClose={() => setReviewing(null)}
        onDecided={() => {
          load().catch(() => undefined)
        }}
      />
    </>
  )
}
